import type { Path } from 'react-router';
import { useNavigate } from 'react-router';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { toProjects } from '@gonfalon/navigator/navigators';

import { useDispatch } from '#hooks/useDispatch';
import type { GetState, GlobalDispatch, GlobalState } from '#reducers/state';
import {
  deleteProject as projectAPIDeleteProject,
  fetchProjectEnvironments as projectAPIFetchProjectEnvironments,
  getAllProjects as projectAPIGetAllProjects,
  getProject as projectAPIGetProject,
} from '#sources/ProjectAPI';
import type {
  NormalizedEnvironmentsCollectionResponse,
  NormalizedProjectCollectionResponse,
  NormalizedProjectResponse,
} from '#utils/projects/projectTypes';
import type { EnvironmentsFilters, ProjectFilters } from '#utils/projects/records/Filters';
import { createEnvironmentsFilters } from '#utils/projects/records/Filters';
import type { Project } from '#utils/projects/records/Project';
import type { GenerateActionType } from '#utils/reduxUtils';

const requestProjects = () =>
  ({
    type: 'projects/REQUEST_PROJECTS',
  }) as const;

const requestProject = (projectKey: string) =>
  ({
    type: 'projects/REQUEST_PROJECT',
    projectKey,
  }) as const;

const requestProjectsDone = (response: NormalizedProjectCollectionResponse) =>
  ({
    type: 'projects/REQUEST_PROJECTS_DONE',
    response,
  }) as const;

const requestPaginatedProjects = () =>
  ({
    type: 'projects/REQUEST_PAGINATED_PROJECTS',
  }) as const;

const requestPaginatedProjectsDone = (response: NormalizedProjectCollectionResponse) =>
  ({
    type: 'projects/REQUEST_PAGINATED_PROJECTS_DONE',
    response,
  }) as const;

const requestPaginatedProjectsFailed = (error: ImmutableServerError) =>
  ({
    type: 'projects/REQUEST_PAGINATED_PROJECTS_FAILED',
    error,
  }) as const;

const requestProjectDone = (projectKey: string, response: NormalizedProjectResponse) =>
  ({
    type: 'projects/REQUEST_PROJECT_DONE',
    projectKey,
    response,
  }) as const;

const requestProjectEnvironments = (projectKey: string) =>
  ({
    type: 'projects/REQUEST_PROJECT_ENVIRONMENTS',
    projectKey,
  }) as const;

const requestProjectEnvironmentsDone = (response: NormalizedEnvironmentsCollectionResponse) =>
  ({
    type: 'projects/REQUEST_PROJECT_ENVIRONMENTS_DONE',
    response,
  }) as const;

const requestProjectEnvironmentsFailed = (error: ImmutableServerError) =>
  ({
    type: 'projects/REQUEST_PROJECT_ENVIRONMENTS_FAILED',
    error,
  }) as const;

const requestProjectsFailed = (error: ImmutableServerError) =>
  ({
    type: 'projects/REQUEST_PROJECTS_FAILED',
    error,
  }) as const;
const requestProjectFailed = (projectKey: string, error: ImmutableServerError) =>
  ({
    type: 'projects/REQUEST_PROJECT_FAILED',
    projectKey,
    error,
  }) as const;

export function fetchProject(projectKey: string) {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestProject(projectKey));
    return new Promise((resolve, reject) => {
      projectAPIGetProject(projectKey)
        .then((response) => {
          dispatch(requestProjectDone(projectKey, response));
          resolve(response);
        })
        .catch((error) => {
          dispatch(requestProjectFailed(projectKey, error));
          reject(error);
        });
    });
  };
}

function fetchProjects() {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestProjects());
    return new Promise<NormalizedProjectCollectionResponse>((resolve, reject) => {
      projectAPIGetAllProjects()
        .then((response) => {
          dispatch(requestProjectsDone(response));
          resolve(response);
        })
        .catch((error) => {
          dispatch(requestProjectsFailed(error));
          reject(error);
        });
    });
  };
}

function _fetchProjectEnvironments(projectKey: string, filters?: EnvironmentsFilters) {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestProjectEnvironments(projectKey));
    return new Promise((resolve, reject) => {
      projectAPIFetchProjectEnvironments(projectKey, createEnvironmentsFilters(filters))
        .then((response) => {
          dispatch(requestProjectEnvironmentsDone(response));
          resolve(response);
        })
        .catch((error) => {
          dispatch(requestProjectEnvironmentsFailed(error));
          reject(error);
        });
    });
  };
}

export function fetchProjectEnvironments(projectKey: string, filters?: EnvironmentsFilters) {
  return (dispatch: GlobalDispatch) => dispatch(_fetchProjectEnvironments(projectKey, filters));
}

function shouldFetchProjects(state: GlobalState) {
  return !state.projects.get('lastFetched') && !state.projects.get('isFetching') && !state.projects.get('doNotFetch');
}

function fetchProjectsIfNeeded() {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchProjects(getState())) {
      return dispatch(fetchProjects());
    } else {
      return Promise.resolve();
    }
  };
}

export { fetchProjectsIfNeeded as fetchProjects };

const del = (project: Project) =>
  ({
    type: 'projects/DELETE_PROJECT',
    project,
  }) as const;

const deleteDone = (project: Project) =>
  ({
    type: 'projects/DELETE_PROJECT_DONE',
    project,
  }) as const;

const deleteFailed = (project: Project, error: ImmutableServerError) =>
  ({
    type: 'projects/DELETE_PROJECT_FAILED',
    project,
    error,
  }) as const;

export function useDeleteProject() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (project: Project, filters: ProjectFilters, redirectUrl?: Path) => {
    const projectsUrl = toProjects({ search: filters.toQueryString() });
    const redirect = redirectUrl ?? projectsUrl;
    dispatch(del(project));
    return projectAPIDeleteProject(project)
      .then(async () => {
        await navigate(redirect);
        dispatch(deleteDone(project));
      })
      .catch(async (error) => {
        if (error.get('status') === 403 || error.get('status') === 404) {
          await navigate(redirect);
        }
        dispatch(deleteFailed(project, error));
      });
  };
}

const ProjectActionCreators = {
  requestPaginatedProjects,
  requestPaginatedProjectsDone,
  requestPaginatedProjectsFailed,
  requestProjects,
  requestProjectsDone,
  requestProject,
  requestProjectDone,
  requestProjectFailed,
  requestProjectsFailed,
  requestProjectEnvironments,
  requestProjectEnvironmentsDone,
  requestProjectEnvironmentsFailed,
  del,
  deleteDone,
  deleteFailed,
};

export type ProjectAction = GenerateActionType<typeof ProjectActionCreators>;
